import { IconButton, Tooltip, useDisclosure, useToast } from '@chakra-ui/react';
import DeleteSharpIcon from '@gamma/ui/icons/DeleteSharpIcon';
import { DeleteModal } from '@gamma/ui/overlay';
import { LoadingSpinner } from '@gamma/ui/progress';
import { useApolloClient } from '@apollo/client';
import { i18n } from '../../../../../../../shared/localization';
import {
  ExcludedEntity,
  QUERY_ALERT_METADATA_EXCLUDED_ENTITIES_PAGINATED,
  useRemoveExcludedEntitiesFromAlertMetadata,
} from '../../../../../queries';

import { QueryVariables } from '../../';

interface DeleteModalRowProps {
  title: string;
  type: string;
  tenant?: string;
  content_id?: string;
  original: ExcludedEntity;
  queryVariables: QueryVariables;
}

export const DeleteModalRow = ({
  title,
  type,
  tenant,
  original,
  content_id,
  queryVariables,
}: DeleteModalRowProps) => {
  const disclosure = useDisclosure();
  const showToastMessage = useToast();
  const apolloClient = useApolloClient();

  const {
    actionCannotBeUndone,
    deleteExcluded,
    successRes,
    errorRes,
    entityRemoved,
    entityNotRemoved,
  } = i18n.pages.system.alertCatalog;

  const [removeEntity, { loading: removeLoading }] =
    useRemoveExcludedEntitiesFromAlertMetadata({
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: QUERY_ALERT_METADATA_EXCLUDED_ENTITIES_PAGINATED,
          variables: queryVariables,
        },
      ],
      onCompleted: () => {
        apolloClient.cache.evict({ fieldName: 'queryAlertMetadataPaginated' });
        apolloClient.cache.evict({ fieldName: 'getAlertMetadata' });
        apolloClient.cache.evict({
          fieldName: 'queryAlertMetadataExcludedEntitiesPaginated',
        });
        apolloClient.cache.gc();

        showToastMessage({
          status: 'success',
          title: successRes,
          description: entityRemoved,
          isClosable: true,
          position: 'bottom-right',
        });
      },
      onError: () => {
        showToastMessage({
          status: 'error',
          title: errorRes,
          description: entityNotRemoved,
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  return (
    <DeleteModal
      {...disclosure}
      size="sm"
      body={''}
      hideBody={true}
      isFooterCentered={false}
      data-testid="delete-modal"
      title={deleteExcluded}
      subtitle={actionCannotBeUndone}
      onConfirm={() => {
        const input = {
          content_id,
          tenant,
          entity_name: original?.entity_name,
          entity_type: original?.entity_type,
          alert_name: title,
          alert_type: type,
        };
        if (tenant) {
          removeEntity({
            variables: {
              items: [input],
            },
          });
        }
      }}
    >
      <Tooltip label={'Delete'} placement="bottom-end">
        {removeLoading ? (
          <LoadingSpinner data-testid="delete-loading" size="sm" minH="auto" />
        ) : (
          <IconButton
            size="box-md"
            variant="solid"
            colorScheme="gray"
            icon={<DeleteSharpIcon />}
            aria-label={'Delete'}
            onClick={disclosure.onOpen}
            data-testid="delete-modal-trigger"
          />
        )}
      </Tooltip>
    </DeleteModal>
  );
};
